import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_WARNING } from "@Services/constants";
import { estimatePayloadBytes, hasIndexedDbSpace } from "@Services/utils/indexedDbStorage";
import { store } from "@Store";
import { t } from "@i18n";

const SELECTION_SET_CACHE_DB = "cubeSelectionSet";
const VERSIONS_STORE = "selectionSetItemsVersions";
const ITEMS_STORE = "selectionSetItems";
const ITEMS_BY_SELECTION_SET_INDEX = "by_selectionSetId";
const DB_VERSION = 3;

type VersionEntry = { itemsVersionNo?: number };
/** A row as it crosses the IDB boundary: an object whose individual members are unverified. */
type CacheableItem = Record<string, unknown>;

/** Any non-null object is readable as `Record<string, unknown>`; a primitive is not a cache row. */
function isCacheableItem(value: unknown): value is CacheableItem {
  return typeof value === "object" && value !== null;
}

/** Normalise an IDB / caller payload to the rows this cache stores. */
function toCacheableItems(value: unknown): CacheableItem[] {
  return Array.isArray(value) ? value.filter(isCacheableItem) : [];
}

function openCacheDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(SELECTION_SET_CACHE_DB, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => db.close();
      resolve(db);
    };

    request.onupgradeneeded = () => {
      // `request.result` is the same `IDBDatabase` the upgrade event carries, and it is already typed.
      const db = request.result;
      for (const name of Array.from(db.objectStoreNames)) {
        db.deleteObjectStore(name);
      }
      const itemsStore = db.createObjectStore(ITEMS_STORE, { keyPath: "_id" });
      itemsStore.createIndex(ITEMS_BY_SELECTION_SET_INDEX, "selectionSetId", { unique: false });
      db.createObjectStore(VERSIONS_STORE);
    };
  });
}

// Open a fresh connection scoped to one action and close it as soon as the
// transaction settles. Avoids reusing a handle that another tab's versionchange
// may have put into a closing state.
async function withConnection<T>(
  stores: string[],
  mode: IDBTransactionMode,
  run: (tx: IDBTransaction, resolve: (value: T) => void, reject: (reason?: unknown) => void) => void,
): Promise<T> {
  const db = await openCacheDB();
  return new Promise<T>((resolve, reject) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(stores, mode);
    } catch (error) {
      db.close();
      reject(error);
      return;
    }
    const resolveAndClose = (value: T) => {
      db.close();
      resolve(value);
    };
    const rejectAndClose = (reason?: unknown) => {
      db.close();
      reject(reason);
    };
    tx.onerror = () => rejectAndClose(tx.error);
    tx.onabort = () => rejectAndClose(tx.error);
    try {
      run(tx, resolveAndClose, rejectAndClose);
    } catch (error) {
      rejectAndClose(error);
    }
  });
}

async function ensureIndexedDbSpace(payload: unknown): Promise<boolean> {
  const requiredBytes = estimatePayloadBytes(payload);
  const hasSpace = await hasIndexedDbSpace(requiredBytes);
  if (!hasSpace) {
    store.dispatch(
      showToastNotification({
        content: t("Insufficient storage detected. Clear browser data to Optimize Performance."),
        type: TOAST_TYPE_WARNING,
      }),
    );
    return false;
  }
  return true;
}

// Delete all item rows matching the given selectionSetId. Runs within the caller's transaction;
// onDone fires after the cursor completes so follow-up IDB requests stay in the same tx.
function deleteItemsBySelectionSetId(itemsStore: IDBObjectStore, selectionSetId: string, onDone: () => void): void {
  const index = itemsStore.index(ITEMS_BY_SELECTION_SET_INDEX);
  const cursorReq = index.openCursor(IDBKeyRange.only(selectionSetId));
  cursorReq.onsuccess = () => {
    const cursor = cursorReq.result;
    if (cursor) {
      cursor.delete();
      cursor.continue();
    } else {
      onDone();
    }
  };
}

export async function getCachedSelectionSetItems<T = unknown>(
  selectionSetId: string,
  itemsVersionNo?: number,
): Promise<T[] | null> {
  try {
    return await withConnection<T[] | null>([VERSIONS_STORE, ITEMS_STORE], "readonly", (tx, resolve, reject) => {
      const versionsStore = tx.objectStore(VERSIONS_STORE);
      const itemsStore = tx.objectStore(ITEMS_STORE);
      const versionReq = versionsStore.get(selectionSetId);

      versionReq.onerror = () => reject(versionReq.error);
      versionReq.onsuccess = () => {
        // IDB hands `result` back as `any`; read the one member this lookup needs through `in`.
        const versionEntry: unknown = versionReq.result;
        if (typeof versionEntry !== "object" || versionEntry === null) return resolve(null);
        const cachedVersionNo = "itemsVersionNo" in versionEntry ? versionEntry.itemsVersionNo : undefined;
        if (itemsVersionNo !== undefined && cachedVersionNo !== itemsVersionNo) {
          return resolve(null);
        }
        const index = itemsStore.index(ITEMS_BY_SELECTION_SET_INDEX);
        const itemsReq = index.getAll(IDBKeyRange.only(selectionSetId));
        itemsReq.onerror = () => reject(itemsReq.error);
        itemsReq.onsuccess = () => {
          const rows: unknown = itemsReq.result;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- IDB rows are untyped (`getAll()` is `any`); these are exactly the rows `cacheSelectionSetItems` wrote for this selectionSetId, so the element type is the caller's `T` and nothing provable here.
          resolve(Array.isArray(rows) ? (rows as T[]) : []);
        };
      };
    });
  } catch (error) {
    Promise.reject(error);
    return null;
  }
}

export async function cacheSelectionSetItems(
  selectionSetId: string,
  data: unknown,
  itemsVersionNo?: number,
): Promise<void> {
  try {
    const items = toCacheableItems(data);
    const versionEntry: VersionEntry = { itemsVersionNo };
    if (!(await ensureIndexedDbSpace({ versionEntry, items }))) return;

    await withConnection<void>([VERSIONS_STORE, ITEMS_STORE], "readwrite", (tx, resolve) => {
      tx.oncomplete = () => resolve();
      const versionsStore = tx.objectStore(VERSIONS_STORE);
      const itemsStore = tx.objectStore(ITEMS_STORE);
      deleteItemsBySelectionSetId(itemsStore, selectionSetId, () => {
        for (const item of items) {
          if (typeof item._id !== "string") continue;
          itemsStore.put({ ...item, selectionSetId });
        }
        versionsStore.put(versionEntry, selectionSetId);
      });
    });
  } catch (error) {
    Promise.reject(error);
  }
}

export async function addCachedSelectionSetItems(
  selectionSetId: string,
  items: unknown,
  newItemsVersionNo?: number,
): Promise<void> {
  try {
    const toAdd = toCacheableItems(items);
    if (toAdd.length === 0 && newItemsVersionNo === undefined) return;
    if (!(await ensureIndexedDbSpace({ items: toAdd, newItemsVersionNo }))) return;

    await withConnection<void>([VERSIONS_STORE, ITEMS_STORE], "readwrite", (tx, resolve) => {
      tx.oncomplete = () => resolve();
      const itemsStore = tx.objectStore(ITEMS_STORE);
      for (const item of toAdd) {
        if (typeof item._id !== "string") continue;
        itemsStore.put({ ...item, selectionSetId });
      }
      if (newItemsVersionNo !== undefined) {
        tx.objectStore(VERSIONS_STORE).put({ itemsVersionNo: newItemsVersionNo }, selectionSetId);
      }
    });
  } catch (error) {
    Promise.reject(error);
  }
}

// Delete items by _id from the flat store and bump the version entry in one tx.
export async function removeCachedSelectionSetItemsByIds(
  selectionSetId: string,
  ids: string[],
  newItemsVersionNo?: number,
): Promise<void> {
  try {
    if (ids.length === 0 && newItemsVersionNo === undefined) return;

    await withConnection<void>([VERSIONS_STORE, ITEMS_STORE], "readwrite", (tx, resolve) => {
      tx.oncomplete = () => resolve();
      const itemsStore = tx.objectStore(ITEMS_STORE);
      for (const id of ids) {
        if (typeof id === "string") itemsStore.delete(id);
      }
      if (newItemsVersionNo !== undefined) {
        tx.objectStore(VERSIONS_STORE).put({ itemsVersionNo: newItemsVersionNo }, selectionSetId);
      }
    });
  } catch (error) {
    Promise.reject(error);
  }
}

export async function deleteCachedSelectionSetItems(selectionSetId: string): Promise<void> {
  try {
    await withConnection<void>([VERSIONS_STORE, ITEMS_STORE], "readwrite", (tx, resolve) => {
      tx.oncomplete = () => resolve();
      tx.objectStore(VERSIONS_STORE).delete(selectionSetId);
      deleteItemsBySelectionSetId(tx.objectStore(ITEMS_STORE), selectionSetId, () => {});
    });
  } catch (error) {
    Promise.reject(error);
  }
}

/**
 * Wipes every entry from both selection-set stores. Used by the Viewer
 * Settings "Clear Cache" action. Rows are keyed by `selectionSetId` (not
 * `fileVersionId`), so there is no per-file filter — this clears all.
 */
export async function clearAllSelectionSetItemsCache(): Promise<void> {
  try {
    await withConnection<void>([VERSIONS_STORE, ITEMS_STORE], "readwrite", (tx, resolve) => {
      tx.oncomplete = () => resolve();
      tx.objectStore(VERSIONS_STORE).clear();
      tx.objectStore(ITEMS_STORE).clear();
    });
  } catch (error) {
    Promise.reject(error);
  }
}
